// Context-aware supportive messages.
//
// Instead of one static rotation, we pick from a bucket that matches the
// person's actual situation right now - especially a hard day, which needs
// a gentle, non-judgmental message rather than a generic "you're strong!"
// line that can feel tone-deaf right after a lapse.
//
// Within the right bucket the message rotates by day-of-year, so it still
// changes daily, but it never picks a celebratory message on a day someone
// needed comfort instead. Stable across re-renders on the same day.

const MANTRAS = {
  // Shown the day someone logs a lapse. Never celebratory, never shaming.
  hardDay: [
    'יום קשה לא מוחק את כל הדרך שכבר עברת. מחר הוא התחלה חדשה. 💜',
    'נפילה היא חלק מהדרך, לא הסוף שלה. אתה עדיין כאן, וזה מה שחשוב. 🌱',
    'אל תשפוט את עצמך על יום אחד. מה אתה צריך עכשיו כדי להמשיך? 🤍',
    'הרצף הטוב ביותר שלך עדיין שייך לך. הוא מוכיח שאתה מסוגל. 🌅',
    'כל מי שהחלים אי פעם נפל בדרך. ההבדל הוא בקימה. 💪',
    'זה בסדר שהיה קשה. הצעד הראשון בחזרה הוא המשמעותי ביותר. 🕊️',
    'אתה חוזר להתחיל, לא מתחיל מאפס. כל מה שלמדת נשאר איתך. 🧭',
    'הימים הטובים שהיו לך לא נעלמו, הם עדיין חלק ממך. 🌤️',
  ],
  // Streak is at zero: either the very beginning, or the day after a reset.
  freshStart: [
    'כל מסע מתחיל בצעד אחד. היום הוא הצעד שלך. 🌱',
    'אתה לא מתחיל מאפס, אתה מתחיל עם כל מה שלמדת עד עכשיו. 🌤️',
    'היום הוא הזדמנות חדשה לבחור אחרת. ✨',
    'התהליך מתחיל ביום אחד. זה יכול להיות היום. 🔥',
    'כל בוקר הוא הזדמנות להתחיל מחדש. 🌅',
    'אתה לא חייב להיות מושלם, רק להמשיך לנסות. 🙏',
  ],
  // Building momentum: 1 to 29 days.
  building: [
    'כל יום שאתה ממשיך, אתה בונה משהו אמיתי. 💪',
    'אתה חזק יותר ממה שאתה חושב. 🌟',
    'התקדמות לא חייבת להיות מושלמת כדי להיות אמיתית. 🚶',
    'כל צעד קדימה הוא ניצחון, גם אם הוא קטן. 🌈',
    'כל יום נקי הוא ניצחון חדש. 💜',
    'ההחלמה היא לא קו ישר, והיא עדיין החלמה. 🌱',
    'לפעמים הניצחון הגדול ביותר הוא פשוט להמשיך. 🕊️',
    'אתה בונה משהו אמיתי, יום אחרי יום. 🧱',
    'תן לעצמך קרדיט על הדרך שכבר עברת. 🙏',
    'אתה לא לבד בדרך הזו. ✨',
  ],
  // 30 days and beyond.
  strong: [
    'המחויבות שלך לעצמך ניכרת כאן, יום אחרי יום. 🏆',
    'אתה לא אותו אדם שהתחיל את הדרך הזו, וזה שווה לחגוג. 🦋',
    'אתה בונה חיים שאתה גאה בהם. 🌅',
    'ההתמדה שלך היא ההוכחה הטובה ביותר שאתה יכול לסמוך על עצמך. ⭐',
    'כל יום שצברת הוא הוכחה שאתה מסוגל. 👑',
    'זה לא מזל, זו התמדה. כל הכבוד. 🎉',
  ],
};

const dayOfYear = (date) => {
  const start = new Date(date.getFullYear(), 0, 0);
  return Math.floor((date - start) / 86400000);
};

/**
 * Picks a message matched to the person's current situation.
 * @param {{ streak?: number, todayLog?: { isClean?: boolean } | null }} ctx
 * @returns {string}
 */
export const getMantra = ({ streak = 0, todayLog = null } = {}) => {
  let bucket;
  if (todayLog && todayLog.isClean === false) {
    bucket = MANTRAS.hardDay;
  } else if (streak <= 0) {
    bucket = MANTRAS.freshStart;
  } else if (streak < 30) {
    bucket = MANTRAS.building;
  } else {
    bucket = MANTRAS.strong;
  }
  return bucket[dayOfYear(new Date()) % bucket.length];
};
